import React from 'react';
import moment from 'moment';
import linkifyHtml from 'linkify-html';
import type { Announcement } from './useAnnouncements';
import { Megaphone } from 'lucide-react';
import { Badge } from '@/components/ui/badge';

const typeConfig: Record<
	string,
	{ label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline' }
> = {
	feature: { label: 'New Feature', variant: 'default' },
	news: { label: 'News', variant: 'secondary' },
	alert: { label: 'Alert', variant: 'destructive' },
};

const defaultType = {
	label: 'Update',
	variant: 'secondary' as const,
};

const formatText = (inputText: string) => {
	try {
		const formattedText = linkifyHtml(inputText, {
			target: '_blank',
			rel: 'noopener noreferrer',
			className: 'linkified text-primary underline underline-offset-2',
		});
		return { __html: formattedText };
	} catch {
		const urlRegex = /(https?:\/\/[^\s]+)/g;
		const formattedText = inputText.replace(urlRegex, (url) => {
			return `<a href="${url}" target="_blank" rel="noopener noreferrer" class="linkified text-primary underline underline-offset-2">${url}</a>`;
		});
		return { __html: formattedText };
	}
};

const formatDate = (dateStr: string) => {
	try {
		return moment(dateStr).format('ll');
	} catch {
		return '';
	}
};

interface AnnouncementListProps {
	announcements: Announcement[];
}

export function AnnouncementList({ announcements }: AnnouncementListProps) {
	if (announcements.length === 0) {
		return (
			<div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
				<Megaphone className="size-8 mb-2 opacity-40" />
				<p className="text-sm">No announcements yet.</p>
			</div>
		);
	}

	return (
		<div className="space-y-3">
			{announcements.map((announcement, i) => {
				const config = typeConfig[announcement.type] || defaultType;

				return (
					<div
						key={i}
						className="rounded-lg border bg-card p-4 transition-colors hover:bg-accent/40"
					>
						<h4 className="text-sm font-semibold leading-snug mb-1">
							{announcement.title}
						</h4>
						<div className="flex items-center gap-2 mb-2">
							{announcement.createdAt && (
								<span className="text-xs text-muted-foreground">
									{formatDate(announcement.createdAt)}
								</span>
							)}
							<Badge
								variant={config.variant}
								className="text-[10px] px-1.5 py-0 leading-tight"
							>
								{config.label}
							</Badge>
						</div>
						<div
							dangerouslySetInnerHTML={formatText(
								announcement.details,
							)}
							className="text-[13px] text-muted-foreground leading-relaxed whitespace-pre-wrap"
						/>
					</div>
				);
			})}
		</div>
	);
}
